import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import toast from 'react-hot-toast';

import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { Button } from 'components/button/Button';
import { InputField } from 'components/input/InputField';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { getDisplayError } from '../../../utils/get-display-error';
import { CheckboxField } from '../../../components/checkbox/CheckboxField';
import { useTranslation } from '../../../contexts/translation-context';
import { captureException } from '@sentry/react';
import { createCarrierPortalContact } from '../models';

const createContactSchema = Yup.object().shape({
  name: Yup.string().min(1, 'errors.required').required('errors.required'),
  email: Yup.string().email('errors.invalidEmail'),
  phone: Yup.string().phone(),
  language: Yup.mixed().nullable().required('errors.required'),
  shouldReceiveTransportOrders: Yup.boolean(),
  shouldReceiveSelfBillings: Yup.boolean(),
});

export const CarrierPortalCreateContactPage = () => {
  const navigate = useNavigate();
  const { supplier, carrierId, refreshData: refetchSupplierData } = useCarrierPortalSupplier();
  const { i18n } = useTranslation();

  return (
    <>
      <h1 className="heading-one m-4">{i18n('carrierPortal.contacts.newContact')}</h1>

      <div className="card p-4 mx-4">
        <Formik
          initialValues={{
            name: '',
            email: '',
            phone: '',
            function: '',
            language: LANGUAGE_VALUES.find((v) => v.key === supplier.language)!,
            shouldReceiveTransportOrders: false,
            shouldReceiveSelfBillings: false,
            internalNotes: '',
          }}
          validationSchema={createContactSchema}
          onSubmit={async (values) => {
            try {
              await createCarrierPortalContact(carrierId, {
                name: values.name,
                email: values.email,
                phone: values.phone,
                function: values.function,
                language: values.language.key,
                shouldReceiveTransportOrders: values.shouldReceiveTransportOrders,
                shouldReceiveSelfBillings: values.shouldReceiveSelfBillings,
                internalNotes: values.internalNotes,
              });
              await refetchSupplierData();
              toast.success(i18n('carrierPortal.contacts.createContactSuccess'));
              navigate('..');
            } catch (err) {
              captureException(err);
              toast.error(
                i18n('carrierPortal.contacts.createContactError', {
                  error: getDisplayError(err),
                }),
              );
            }
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <InputField labelText={i18n('carrierPortal.contacts.name')} type="text" name="name" />
              <InputField labelText={i18n('carrierPortal.contacts.email')} type="email" name="email" />
              <InputField labelText={i18n('carrierPortal.contacts.phone')} type="text" name="phone" />
              <InputField labelText={i18n('carrierPortal.contacts.function')} type="text" name="function" />
              <SimpleSelectField
                labelText={i18n('carrierPortal.contacts.language')}
                items={LANGUAGE_VALUES}
                name="language"
              />

              <h2 className="heading-two mb-2">{i18n('carrierPortal.contacts.notifications')}</h2>
              <div className="grid grid-cols-4 gap-x-4 mb-2">
                <CheckboxField
                  labelText={i18n('carrierContactNotifications.transportOrders')}
                  name="shouldReceiveTransportOrders"
                />
                <CheckboxField
                  labelText={i18n('carrierContactNotifications.selfBillings')}
                  name="shouldReceiveSelfBillings"
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button
                  type="submit"
                  color="primary"
                  iconLeft={<PlusIcon className="button-icon" />}
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  {i18n('customerPortal.contacts.createContact')}
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};
